'use client';

/**
 * Enrolment Summary Cards — 4-card grid showing key enrolment metrics.
 * Feature: 014-customer-enrolment-platform (Phase 5, T086)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@kit/ui/card';
import { GraduationCap, CheckCircle2, XCircle, DollarSign } from 'lucide-react';

import type { EnrolmentMetrics } from '../_lib/types';

interface EnrolmentSummaryCardsProps {
  metrics: EnrolmentMetrics;
}

export function EnrolmentSummaryCards({ metrics }: EnrolmentSummaryCardsProps) {
  // totalCreated includes all enrolments (tentative + confirmed).
  // confirmed is a SUBSET of created (created first, confirmed after payment).
  // So "successful" = totalCreated only, not created + confirmed.
  const successRate =
    metrics.totalStarted > 0
      ? Math.round((metrics.totalCreated / metrics.totalStarted) * 100)
      : 0;

  const hasRevenue = metrics.revenueInvoiced > 0;

  const cards = [
    {
      title: 'Total Attempts',
      value: metrics.totalStarted,
      description: 'Enrolment flows started',
      icon: GraduationCap,
      iconColor: 'text-blue-500',
    },
    {
      title: 'Successful',
      value: metrics.totalCreated,
      description: `${successRate}% conversion rate · ${metrics.totalConfirmed} confirmed`,
      icon: CheckCircle2,
      iconColor: 'text-emerald-500',
    },
    {
      title: 'Failed / Abandoned',
      value: metrics.totalFailed,
      description: `${metrics.totalWaitlisted} waitlisted`,
      icon: XCircle,
      iconColor: 'text-red-500',
    },
    {
      title: 'Revenue Invoiced',
      value: hasRevenue
        ? `$${metrics.revenueInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `${metrics.totalPayments} payments`,
      description: hasRevenue
        ? `${metrics.totalConfirmed} paid · ${metrics.totalCreated - metrics.totalConfirmed} awaiting payment`
        : 'No amount data available',
      icon: DollarSign,
      iconColor: 'text-amber-500',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <card.icon className={`h-4 w-4 shrink-0 ${card.iconColor}`} />
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
            <p className="mt-1 text-xs text-muted-foreground">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
